import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { WHATS_NEW_VERSION, CHANGELOG, changelogDescriptionParts } from '../data/changelog'
import './WhatsNewModal.css'

const STORAGE_KEY = `whatsNew_seen_${WHATS_NEW_VERSION}`

const CATEGORY_COLORS = {
  'New Feature': '#6c63ff',
  'Improvement': '#3ecfcf',
  'Fix': '#f0a500'
}

const ChangelogDescription = ({ description }) => (
  <span className='whats-new-item-desc'>
    {changelogDescriptionParts(description).map((part, index) => (
      part.type === 'link'
        ? (
          <a
            key={`${part.href}-${index}`}
            className='whats-new-item-link'
            href={part.href}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${part.text} (opens in a new tab)`}
          >
            {part.text}
          </a>
        )
        : <span key={index}>{part.value}</span>
    ))}
  </span>
)

const WhatsNewModal = () => {
  const { currentUser, userProfile, studentViewActive, parentTaskModalActive } = useAuth()
  const [visible, setVisible] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)

  useEffect(() => {
    if (!currentUser || !userProfile) return
    const seen = localStorage.getItem(STORAGE_KEY)
    if (!seen) {
      setVisible(true)
    }
  }, [currentUser, userProfile])

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true')
    setVisible(false)
  }

  // Locked decision #13 (overview doc): Competition / What's New wait until
  // the blocking name modal and the post-name Add-student/demo choice are
  // done or dismissed. Both are ParentDashboard-only concerns today, but this
  // modal is mounted at the App shell level (always present, on every route),
  // so it must read the same two profile flags directly rather than relying
  // on ParentDashboard to suppress it — otherwise a parent who has not seen
  // this release's notes would see them race on top of (or immediately
  // before) the name/choice modals right after signing up.
  //
  // parentTaskModalActive covers what picking a choice leads to (the demo,
  // Add Student) and their later reopen paths (checklist, Settings, side
  // menu): the choice itself resolves as soon as a path is picked, but the
  // release notes still have no business covering a demo the parent is
  // running with their child watching, or a half-filled Add Student form.
  //
  // Parent-facing release notes also have no business appearing over a
  // child's work while the device is in Student View. In every case here the
  // modal is not dismissed, only deferred: it shows the next time the parent
  // has the screen and none of the above still hold.
  const blockingOnboardingSurface = userProfile?.pendingParentName === true ||
    userProfile?.pendingOnboardingChoice === true ||
    userProfile?.pendingStudentUsername === true

  if (!visible || studentViewActive || blockingOnboardingSurface || parentTaskModalActive) return null

  const currentEntry = CHANGELOG[pageIndex]
  const isLatest = pageIndex === 0
  const isOldest = pageIndex === CHANGELOG.length - 1

  return (
    <div className='whats-new-overlay' onClick={handleDismiss}>
      <div className='whats-new-modal' onClick={(e) => e.stopPropagation()}>
        <div className='whats-new-header'>
          <div className='whats-new-badge'>{currentEntry.version}</div>
          <h2 className='whats-new-title'>What's New in Quillnote</h2>
          <p className='whats-new-subtitle'>
            {isLatest
              ? "Here's what changed since your last visit."
              : `Changes introduced in version ${currentEntry.version}.`}
          </p>
        </div>

        <ul className='whats-new-list'>
          {currentEntry.changes.map((change, index) => (
            <li key={index} className='whats-new-item'>
              <span
                className='whats-new-category'
                style={{ backgroundColor: CATEGORY_COLORS[change.category] || '#888' }}
              >
                {change.category}
              </span>
              <div className='whats-new-text'>
                <span className='whats-new-item-title'>{change.title}</span>
                <ChangelogDescription description={change.description} />
              </div>
            </li>
          ))}
        </ul>

        <div className='whats-new-nav'>
          <button
            className='whats-new-nav-btn'
            onClick={() => setPageIndex(pageIndex + 1)}
            disabled={isOldest}
          >
            ← Older
          </button>
          <span className='whats-new-nav-indicator'>
            {pageIndex + 1} / {CHANGELOG.length}
          </span>
          <button
            className='whats-new-nav-btn'
            onClick={() => setPageIndex(pageIndex - 1)}
            disabled={isLatest}
          >
            Newer →
          </button>
        </div>

        {isLatest && (
          <button className='whats-new-dismiss' onClick={handleDismiss}>
            Got it
          </button>
        )}
      </div>
    </div>
  )
}

export default WhatsNewModal
